import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { APP_ENV, ENV_NAMES } from "@utils/env";
import { ClientError } from "graphql-request";

import { DevOnlyWrapper } from "@components/DevOnlyWrapper/DevOnlyWrapper";
import { notifyError, notifyWarn } from "@components/notifications/notifications";

let logoutInProgress = false;

/** @internal Test-only. Reset module state between tests. In production, logout redirects clear this naturally. */
export const _resetAuthState = () => {
  logoutInProgress = false;
};

export const useAuthorization = () => {
  const { getAccessTokenSilently, logout } = useAuth0();

  const getAuthorizationHeader = async (): Promise<Headers> => {
    let token: string = "";
    try {
      /**
       * Auth0 internally via this SDK (getAccessTokenSilently) will simply retrieve the cached access_token from storage and return that
       * if that token is not expired. This means we can call this a million times and no requests out to Auth0 will be made
       *
       * If the cached access_token is within 1 minute of expiring, it will automatically make a request to /oauth/token for a new
       * access_token using the cached refresh_token. if that refresh_token is expired, this will throw an error and we will redirect
       * the user to logout
       */
      token = await getAccessTokenSilently();
    } catch (err) {
      console.error("Error with getAccessTokenSilently", err);
      datadogRum.addError(err, { type: "getAccessTokenSilentlyError" });
      await logout({
        logoutParams: {
          returnTo: `${window.location.protocol}//${window.location.host}`,
        },
      });
      throw new Error(String(err));
    }

    const headers: Headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);
    return headers;
  };

  /**
   * PROA-1818: this deliberately does not take the GraphQL `variables`. They routinely
   * carry PHI (firstName, lastName, dateOfBirth, goalName, masteryCriteria), and the
   * Datadog RUM error context below is egress to a third party. Not accepting them at
   * all is a stronger guarantee than accepting and remembering not to forward them.
   *
   * `query` is safe and is kept so RUM errors stay attributable to an operation. The
   * dev-only toast further down still shows variables, but it reads them off
   * `err.request` and renders in the user's own browser — not a Datadog path.
   */
  const handleApiError = async (err: Error, query: string) => {
    const errorString = String(err);

    /**
     * If FDM errors with Unauthorized or User not found, log for telemetry and redirect to login.
     *
     * "Not Authorized" = invalid/expired token (shouldn't happen much due to getAccessTokenSilently above).
     * "User not found" = Auth0 session is valid but the user has no Auth0User record in FDM (e.g. user
     * was created in Auth0 but never provisioned in FDM, or the FDM record was deleted). Without a
     * backend user record every subsequent query will also 404, so logging out breaks the error loop.
     */
    if (errorString.includes("Not Authorized") || errorString.includes("User not found")) {
      const isUserNotFound = errorString.includes("User not found");
      const errorType = isUserNotFound ? "fdmUserNotFoundError" : "fdmNotAuthorizedError";
      datadogRum.addError(err, { type: errorType, requestMetadata: { query } });

      if (logoutInProgress) {
        throw new Error(errorString);
      }
      logoutInProgress = true;

      if (isUserNotFound) {
        notifyWarn(
          "You've logged in successfully, but your account isn't fully set up yet. Please contact your organization administrator to complete your access.",
          { autoClose: 4000 },
        );
        await new Promise((resolve) => setTimeout(resolve, 4000));
      }

      try {
        await logout({
          logoutParams: {
            returnTo: `${window.location.protocol}//${window.location.host}`,
          },
        });
      } catch {
        logoutInProgress = false;
      }
      throw new Error(errorString);
    }

    /**
     * Send an actual Datadog Error (a more first class citizen) on all FDM API errors
     */
    datadogRum.addError(err, { type: "fdmApiRequestError", requestMetadata: { query } });
    console.error("Error with request to FDM", err);
    console.error(err);
    /**
     * Only show a toast when the server returned actual GraphQL errors in the response body.
     * ClientError with response.errors means FDM explicitly returned application errors.
     * HTTP-level errors (502/503 from load balancers during network changes), network
     * failures, and other browser/Chrome-level issues won't have response.errors and
     * should not surface to the user as toasts.
     */
    if (err instanceof ClientError && err.response.errors?.length) {
      // Surface the first server-authored error message inline so users
      // see actionable text (e.g. "This insurance package targets the
      // same payor(s) as Frontera Tricare. Disable the other one first.")
      // instead of a generic wrapper. Fall back to the wrapper string if
      // the message is missing.
      const primaryMessage = err.response.errors[0]?.message ?? "Something went wrong with our API request";
      notifyError(
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-1">
            <p>{primaryMessage}</p>
            <DevOnlyWrapper>
              {err.response.errors.map((error, index) => (
                <div key={index} className="flex flex-col gap-0.5">
                  <p className="text-sm opacity-90">
                    {error.path && <b>{error.path.join(".")}:</b>} {error.message}
                  </p>

                  <div className="grid grid-rows-[1fr] overflow-hidden">
                    <pre className="line-clamp-2 overflow-auto rounded bg-white/20 px-1 py-0.5 font-mono text-[10px]">
                      {err.request.query.toString().trim()}
                    </pre>
                  </div>

                  {err.request.variables && !!Object.keys(err.request.variables).length && (
                    <div className="grid grid-rows-[1fr] overflow-hidden bg-white/20 px-1 py-0.5 font-mono text-[10px]">
                      {Object.entries(err.request.variables as Record<string, unknown>).map(([key, value]) => (
                        <p key={key}>
                          <b>{key}:</b> {JSON.stringify(value)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </DevOnlyWrapper>
          </div>
        </div>,
        {
          ...([ENV_NAMES.LOCAL].includes(APP_ENV)
            ? {
                // Keep the toast on screen long enough to read the rendered query and variables.
                autoClose: 30000,
              }
            : {}),
          // Deterministic id collapses duplicate toasts when the same request is retried.
          toastId: err.request.query + JSON.stringify(err.request.variables),
        },
      );
    } else if ([ENV_NAMES.DEV, ENV_NAMES.LOCAL].includes(APP_ENV)) {
      notifyError("Something went wrong with our API request");
    }

    /**
     * Rethrow the error so that downstream callers of this can handle their own error handling
     */
    throw new Error(String(err));
  };

  return { getAuthorizationHeader, handleApiError };
};
